/* eslint-disable linebreak-style */
/* eslint-disable import/extensions */
/* eslint-disable react/jsx-filename-extension */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import emailjs from '@emailjs/browser';

import Header from 'parts/Header';
import Footer from 'parts/Footer';

import { JobOpenings } from 'json/landingPageData';

import ParticleBackground from '../elements/ParticleBackground';
import { uploadResume } from '../utils/cloudinary';

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_APPLY_TEMPLATE;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

// Defaults (Business Development form); a job can override via `ratedSkills` / `questions`.
const DEFAULT_SKILLS_TO_RATE = [
  'Research/Job Hunting',
  'Platforms Algorithm Knowledge',
  'Negotiation',
  'Work Independently',
  'CRM Tools',
  'Client Communication',
];

const DEFAULT_QUESTIONS = [
  { name: 'q1', label: 'Experience in C2C (years) *' },
  { name: 'q2', label: 'List down lead generation platforms you worked. *' },
];

const buildInitialState = (skills) => ({
  fullName: '',
  email: '',
  contact: '',
  linkedin: '',
  gender: '',
  city: '',
  joinOnsite: '',
  commute: '',
  nightShift: '',
  eveningShift: '',
  university: '',
  gradYear: '',
  cgpa: '',
  ratings: skills.reduce((acc, s) => ({ ...acc, [s]: '' }), {}),
  q1: '',
  q2: '',
  notice: '',
  currentSalary: '',
  expectedSalary: '',
});

export default function JobDetailPage() {
  const { id } = useParams();
  const job = JobOpenings.find((j) => j.id === id);
  const skillsToRate = job?.ratedSkills || DEFAULT_SKILLS_TO_RATE;
  const questions = job?.questions || DEFAULT_QUESTIONS;
  const [form, setForm] = useState(() => buildInitialState(skillsToRate));
  const [resumeFile, setResumeFile] = useState(null);
  const [status, setStatus] = useState({ state: 'idle', step: '', message: '' });

  useEffect(() => {
    window.scrollTo(0, 0);
    setForm(buildInitialState(skillsToRate));
    setStatus({ state: 'idle', step: '', message: '' });
  }, [id]);

  if (!job) {
    return (
      <div className="neon-theme min-h-screen">
        <Header />
        <ParticleBackground />
        <section className="container mx-auto px-4 lg:px-8 pt-32 pb-20 relative z-10 text-center">
          <h1 className="text-3xl text-white mb-4">Job not found</h1>
          <NavLink to="/careers" className="hero-cta-primary">Back to All Jobs</NavLink>
        </section>
        <Footer />
      </div>
    );
  }

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRating = (skill, value) => {
    setForm((prev) => ({ ...prev, ratings: { ...prev.ratings, [skill]: value } }));
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0] || null;
    setResumeFile(file);
  };

  const flattenRatings = (ratings) => Object.entries(ratings)
    .map(([k, v]) => `${k}: ${v || '—'}`)
    .join('\n');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      setStatus({ state: 'error', step: '', message: 'Application service is not configured.' });
      return;
    }
    if (!resumeFile) {
      setStatus({ state: 'error', step: '', message: 'Please attach your resume (PDF) before submitting.' });
      return;
    }

    try {
      setStatus({ state: 'sending', step: 'uploading', message: 'Uploading your resume…' });
      const resumeUrl = await uploadResume(resumeFile);

      setStatus({ state: 'sending', step: 'sending', message: 'Submitting your application…' });
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          job_title: job.title,
          full_name: form.fullName,
          email: form.email,
          contact: form.contact,
          linkedin: form.linkedin,
          gender: form.gender,
          city: form.city,
          join_onsite: form.joinOnsite,
          commute: form.commute,
          night_shift: form.nightShift,
          evening_shift: form.eveningShift,
          university: form.university,
          grad_year: form.gradYear,
          cgpa: form.cgpa,
          skill_ratings: flattenRatings(form.ratings),
          // Existing EmailJS template slots, filled positionally from this job's questions.
          years_c2c: form.q1 || '—',
          lead_gen_platforms: form.q2 || '—',
          additional_answers: questions
            .map((q) => `${q.label.replace(/\s*\*$/, '')}\n${form[q.name] || '—'}`)
            .join('\n\n'),
          notice: form.notice,
          current_salary: form.currentSalary,
          expected_salary: form.expectedSalary,
          resume_url: resumeUrl,
          reply_to: form.email,
        },
        { publicKey: PUBLIC_KEY },
      );

      setStatus({
        state: 'success',
        step: '',
        message: `Thanks for applying to ${job.title}! We’ll review and get back to you.`,
      });
      setForm(buildInitialState(skillsToRate));
      setResumeFile(null);
      const fileInput = document.getElementById('resume');
      if (fileInput) fileInput.value = '';
    } catch (err) {
      setStatus({
        state: 'error',
        step: '',
        message: err?.message || 'Something went wrong. Please email careers@web7labs.com directly.',
      });
    }
  };

  const scrollToForm = (e) => {
    e.preventDefault();
    const el = document.getElementById('apply-form');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isSending = status.state === 'sending';

  return (
    <div className="neon-theme min-h-screen">
      <Header />
      <ParticleBackground />

      <section className="container mx-auto px-4 lg:px-8 pt-28 pb-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          <NavLink to="/careers" className="job-detail-back">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Jobs
          </NavLink>

          {/* Header card */}
          <div className="job-detail-header">
            <div>
              <h1>{job.title}</h1>
              <div className="job-detail-header-pills">
                <span className="job-pill">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a6 6 0 00-6 6c0 4.5 6 10 6 10s6-5.5 6-10a6 6 0 00-6-6zm0 8a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                  {job.location}
                </span>
                <span className="job-pill is-openings">
                  {job.openings} {job.openings === 1 ? 'opening' : 'openings'}
                </span>
                {job.summary?.Experience && (
                  <span className="job-pill">{job.summary.Experience}</span>
                )}
              </div>
            </div>
            <a href="#apply-form" onClick={scrollToForm} className="job-detail-submit">
              Submit Application
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </a>
          </div>

          {/* Two-column: content + sidebar */}
          <div className="job-detail-grid">
            <div>
              <div className="job-detail-card">
                <h2>Job Description</h2>
                <p>{job.fullDescription}</p>
              </div>

              <div className="job-detail-card">
                <h2>Responsibilities</h2>
                <ul className="job-detail-list">
                  {job.responsibilities.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </div>

              <div className="job-detail-card">
                <h2>Requirements</h2>
                <ul className="job-detail-list">
                  {job.requirements.map((r) => <li key={r}>{r}</li>)}
                </ul>
              </div>

              {job.niceToHave?.length > 0 && (
                <div className="job-detail-card">
                  <h2>Nice to Have</h2>
                  <ul className="job-detail-list">
                    {job.niceToHave.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              )}

              {job.benefits?.length > 0 && (
                <div className="job-detail-card">
                  <h2>What You&apos;ll Get</h2>
                  <ul className="job-detail-list">
                    {job.benefits.map((r) => <li key={r}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>

            <aside className="job-detail-sidebar">
              <div className="job-summary-card">
                <h3>Job Summary</h3>
                <div className="job-summary-row">
                  <span>Location:</span>
                  <span>{job.location}</span>
                </div>
                {Object.entries(job.summary || {}).map(([k, v]) => (
                  <div key={k} className="job-summary-row">
                    <span>{k}:</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>

              {job.skills?.length > 0 && (
                <div className="job-summary-card">
                  <h3>Required Skills</h3>
                  <div className="required-skills-tags">
                    {job.skills.map((s) => (
                      <span key={s} className="required-skill-tag">{s}</span>
                    ))}
                  </div>
                </div>
              )}
            </aside>
          </div>

          {/* Apply form */}
          {status.state === 'success' ? (
            <div className="apply-form mx-auto text-center" id="apply-form">
              <div className="text-5xl mb-3">✓</div>
              <h2 className="text-2xl font-bold text-white mb-2">Application submitted</h2>
              <p className="text-blue-100 mb-6">{status.message}</p>
              <NavLink to="/careers" className="hero-cta-primary">
                Back to All Jobs
              </NavLink>
            </div>
          ) : (
            <form id="apply-form" className="apply-form mx-auto" onSubmit={handleSubmit}>
              <h2>Apply for {job.title}</h2>

              <div className="apply-section">
                <div className="apply-section-title">Personal Information</div>
                <div className="apply-grid">
                  <div className="apply-field">
                    <label htmlFor="fullName">Full Name *</label>
                    <input id="fullName" name="fullName" value={form.fullName} onChange={handleChange} required disabled={isSending} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="email">Email *</label>
                    <input id="email" name="email" type="email" value={form.email} onChange={handleChange} required disabled={isSending} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="contact">Contact Number (WhatsApp) *</label>
                    <input id="contact" name="contact" placeholder="+92-305-3239562" value={form.contact} onChange={handleChange} required disabled={isSending} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="linkedin">LinkedIn URL *</label>
                    <input id="linkedin" name="linkedin" placeholder="https://linkedin.com/in/yourprofile" value={form.linkedin} onChange={handleChange} required disabled={isSending} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="gender">Gender *</label>
                    <select id="gender" name="gender" value={form.gender} onChange={handleChange} required disabled={isSending}>
                      <option value="">Select gender</option>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Prefer not to say</option>
                    </select>
                  </div>
                  <div className="apply-field">
                    <label htmlFor="city">Your City *</label>
                    <input id="city" name="city" placeholder="Search city..." value={form.city} onChange={handleChange} required disabled={isSending} />
                  </div>
                </div>
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Job Requirements</div>
                {[
                  { name: 'joinOnsite', label: `Will you be able to join onsite jobs in ${job.location.split(',')[0]}? *` },
                  { name: 'commute', label: 'Can Commute to office daily? *' },
                  { name: 'nightShift', label: 'Comfortable to work in Night shift if ever required? *' },
                  { name: 'eveningShift', label: 'Comfortable to work from 6pm onward? *' },
                ].map((q) => (
                  <div key={q.name} className="apply-field">
                    <label>{q.label}</label>
                    <div className="apply-radio-row">
                      <label className="apply-radio">
                        <input type="radio" name={q.name} value="Yes" checked={form[q.name] === 'Yes'} onChange={handleChange} required disabled={isSending} />
                        <span>Yes</span>
                      </label>
                      <label className="apply-radio">
                        <input type="radio" name={q.name} value="No" checked={form[q.name] === 'No'} onChange={handleChange} disabled={isSending} />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Education</div>
                <div className="apply-grid">
                  <div className="apply-field">
                    <label htmlFor="university">Name of University *</label>
                    <input id="university" name="university" placeholder="Search university..." value={form.university} onChange={handleChange} required disabled={isSending} />
                  </div>
                  <div className="apply-field">
                    <label htmlFor="gradYear">Graduation Year *</label>
                    <select id="gradYear" name="gradYear" value={form.gradYear} onChange={handleChange} required disabled={isSending}>
                      <option value="">Select graduation year</option>
                      {Array.from({ length: 12 }, (_, i) => 2018 + i).map((y) => (
                        <option key={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="apply-field">
                  <label htmlFor="cgpa">CGPA *</label>
                  <input id="cgpa" name="cgpa" placeholder="e.g. 3.5" value={form.cgpa} onChange={handleChange} required disabled={isSending} />
                </div>
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Skills Assessment</div>
                {skillsToRate.map((skill) => (
                  <div key={skill} className="skill-rating-row">
                    <span className="skill-rating-label">Rate your skill in {skill} *</span>
                    <div className="skill-rating-options">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <label key={n}>
                          <input
                            type="radio"
                            name={`skill-${skill}`}
                            value={n}
                            checked={form.ratings[skill] === String(n)}
                            onChange={() => handleRating(skill, String(n))}
                            required
                            disabled={isSending}
                          />
                          {n}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Additional Questions</div>
                {questions.map((q) => (
                  <div key={q.name} className="apply-field">
                    <label htmlFor={q.name}>{q.label}</label>
                    <textarea id={q.name} name={q.name} placeholder="Please provide your answer..." value={form[q.name]} onChange={handleChange} required disabled={isSending} />
                  </div>
                ))}
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Employment</div>
                <div className="apply-grid">
                  <div className="apply-field">
                    <label htmlFor="notice">What is your Notice Period? *</label>
                    <select id="notice" name="notice" value={form.notice} onChange={handleChange} required disabled={isSending}>
                      <option value="">Select notice period</option>
                      <option>Immediate</option>
                      <option>2 weeks</option>
                      <option>1 month</option>
                      <option>2 months</option>
                      <option>3 months</option>
                    </select>
                  </div>
                  <div className="apply-field">
                    <label htmlFor="currentSalary">Current Salary in PKR *</label>
                    <input id="currentSalary" name="currentSalary" placeholder="e.g. 50000" value={form.currentSalary} onChange={handleChange} required disabled={isSending} />
                  </div>
                </div>
                <div className="apply-field">
                  <label htmlFor="expectedSalary">Expected Salary/Stipend in PKR *</label>
                  <input id="expectedSalary" name="expectedSalary" placeholder="e.g. 60000" value={form.expectedSalary} onChange={handleChange} required disabled={isSending} />
                </div>
              </div>

              <div className="apply-section">
                <div className="apply-section-title">Resume</div>
                <div className="apply-field">
                  <label htmlFor="resume">Resume * — Upload 1 supported file: PDF. Max 10 MB.</label>
                  <input id="resume" name="resume" type="file" accept="application/pdf,.pdf" className="apply-file" onChange={handleFile} required disabled={isSending} />
                  {resumeFile && (
                    <div className="text-blue-200 text-sm mt-2">Selected: {resumeFile.name}</div>
                  )}
                </div>
              </div>

              {status.state === 'error' && (
                <div className="form-status-error">{status.message}</div>
              )}
              {isSending && (
                <div className="form-status-info">{status.message}</div>
              )}

              <div className="apply-submit-row">
                <button type="submit" className="apply-submit-btn" disabled={isSending}>
                  {isSending ? 'Submitting…' : 'Submit Application'}
                  {!isSending && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
